import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ShieldCheck, Check, AlertCircle } from 'lucide-react';
import { COUNTRIES, iso2ToFlag, MOBILE_LENGTH_BY_ISO } from '@/lib/satisfinderCountries';
import { SATISFINDER_AGREEMENT } from '@/lib/satisfinderAgreement';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /satisfinder/signup — Code-of-conduct modal → sign-up form → OTP redirect.
 *
 * Feb 2026 streamlined form:
 *   • Name captured as first / (optional middle) / last. full_name is
 *     derived server-side for backwards compatibility.
 *   • No Philippines/+63 default. Country blank; picking a country
 *     auto-populates the mobile country code.
 *   • Global country list; availability driven by backend country-status.
 *   • Postal code required and auto-populates City via the existing
 *     /satisfinder/postal-area-suggest endpoint (with manual fallback).
 *   • Area / Gender / Age / Marketing / Legacy-claim removed from the form.
 *   • Existing account: friendly outcome with Log in / Reset password.
 *   • Code of Conduct is accepted in the modal only — the final
 *     acceptance covers Terms + Privacy (no double Code-of-Conduct ask).
 */
export default function SatisfinderSignup() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const referralCode = params.get('ref') || '';

  const [acceptHonest, setAcceptHonest] = useState(false);
  const [acceptConduct, setAcceptConduct] = useState(false);
  const [showForm, setShowForm] = useState(false);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <SignupHeader />
      {!showForm ? (
        <AgreementModal
          acceptHonest={acceptHonest}
          setAcceptHonest={setAcceptHonest}
          acceptConduct={acceptConduct}
          setAcceptConduct={setAcceptConduct}
          onContinue={() => setShowForm(true)}
        />
      ) : (
        <SignupForm
          referralCode={referralCode}
          acceptHonest={acceptHonest}
          acceptConduct={acceptConduct}
          onSignedUp={(email) => {
            nav(`/satisfinder/verify-email?email=${encodeURIComponent(email)}`);
          }}
        />
      )}
    </div>
  );
}

function SignupHeader() {
  return (
    <header className="bg-white border-b border-[#E2E8F0]">
      <div className="max-w-3xl mx-auto px-6 h-14 flex items-center justify-between">
        <Link to="/satisfinder" className="flex items-center gap-2">
          <img src="/satisfind-logo.png" alt="satisFIND" style={{ height: 36, width: 'auto' }} />
        </Link>
        <Link to="/satisfinder/login" className="text-sm text-[#334155] hover:text-[#0F172A]">
          Login
        </Link>
      </div>
    </header>
  );
}

function AgreementModal({ acceptHonest, setAcceptHonest, acceptConduct, setAcceptConduct, onContinue }) {
  return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-sm p-8" data-testid="sf-agreement-modal">
        <div className="flex items-center gap-2 text-[#1A3E6F]">
          <ShieldCheck className="w-5 h-5" />
          <span className="text-[10px] uppercase tracking-widest font-bold">{SATISFINDER_AGREEMENT.eyebrow}</span>
        </div>
        <h1 className="font-['Montserrat'] text-2xl lg:text-3xl font-bold mt-2">
          {SATISFINDER_AGREEMENT.title}
        </h1>
        <p className="text-sm text-[#475569] mt-4 leading-relaxed">
          {SATISFINDER_AGREEMENT.signupIntro}
        </p>
        <p className="text-sm text-[#475569] mt-3 leading-relaxed">
          {SATISFINDER_AGREEMENT.summary}
        </p>
        <p className="text-sm font-semibold text-[#0F172A] mt-5">{SATISFINDER_AGREEMENT.commitmentsHeading}</p>
        <ul className="mt-3 space-y-2.5">
          {SATISFINDER_AGREEMENT.commitments.map((r, i) => (
            <li key={i} className="flex items-start gap-2.5 text-sm text-[#334155]">
              <Check className="w-4 h-4 text-[#1A3E6F] mt-0.5 shrink-0" />
              <span>{r}</span>
            </li>
          ))}
        </ul>
        <div className="mt-7 space-y-4">
          <label className="flex items-start gap-2.5 cursor-pointer" data-testid="sf-ack-honest-label">
            <input
              type="checkbox"
              checked={acceptHonest}
              onChange={(e) => setAcceptHonest(e.target.checked)}
              className="mt-1 w-4 h-4 accent-[#1A3E6F]"
              data-testid="sf-ack-honest"
            />
            <span className="text-sm text-[#0F172A]">
              {SATISFINDER_AGREEMENT.acknowledgements[0]}
            </span>
          </label>
          <label className="flex items-start gap-2.5 cursor-pointer" data-testid="sf-ack-conduct-label">
            <input
              type="checkbox"
              checked={acceptConduct}
              onChange={(e) => setAcceptConduct(e.target.checked)}
              className="mt-1 w-4 h-4 accent-[#1A3E6F]"
              data-testid="sf-ack-conduct"
            />
            <span className="text-sm text-[#0F172A]">
              {SATISFINDER_AGREEMENT.acknowledgements[1]}
            </span>
          </label>
        </div>
        <button
          onClick={onContinue}
          disabled={!acceptHonest || !acceptConduct}
          className="mt-6 w-full bg-[#1A3E6F] hover:bg-[#12305A] disabled:bg-[#94A3B8] text-white font-semibold py-3 rounded-lg text-sm transition-colors"
          data-testid="sf-agreement-continue"
        >
          I Agree and Continue
        </button>
      </div>
    </div>
  );
}

function SignupForm({ referralCode, acceptHonest, acceptConduct, onSignedUp }) {
  const [legalRefs, setLegalRefs] = useState({});   // { terms_of_service: {path,title,…}, ... }
  // Fetch CMS-backed legal URLs (Feb 2026 CMS). Falls back to /satisfinder/legal/*
  // static routes when a specific legal doc hasn't been mapped yet.
  useEffect(() => {
    fetch(`${API}/public/cms/legal-references`)
      .then(r => r.ok ? r.json() : { references: {} })
      .then(d => setLegalRefs(d.references || {}))
      .catch(() => {});
  }, []);
  const legalUrl = (key, fallback) => (legalRefs[key]?.path) || fallback;
  const [form, setForm] = useState({
    first_name: '',
    middle_name: '',
    last_name: '',
    email: '',
    country_code: '',
    mobile_number: '',
    country: '',
    postal_code: '',
    city: '',
    password: '',
    confirm_password: '',
    is_adult: false,
    accept_legal: false,
  });
  const [countryStatus, setCountryStatus] = useState(null); // 'open' | 'waitlist' | 'blocked' | null
  const [citySuggestions, setCitySuggestions] = useState([]); // [{city, area}]
  const [postalLookupState, setPostalLookupState] = useState('idle'); // 'idle'|'loading'|'no-match'|'matched'
  const [submitting, setSubmitting] = useState(false);
  const [waitlistDone, setWaitlistDone] = useState(false);
  const [existingAccount, setExistingAccount] = useState(false);
  const [existingAccountEmail, setExistingAccountEmail] = useState('');
  const [emailCheckState, setEmailCheckState] = useState('idle'); // 'idle'|'checking'|'available'|'exists'

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const activeCountry = useMemo(() => COUNTRIES.find(x => x.name === form.country) || null, [form.country]);
  const flag = activeCountry ? iso2ToFlag(activeCountry.iso) : '';
  const mobileBand = activeCountry ? (MOBILE_LENGTH_BY_ISO[activeCountry.iso] || [6, 15]) : [6, 15];
  const mobileDigits = (form.mobile_number.match(/\d/g) || []).length;
  const mobileTooShort = mobileDigits > 0 && mobileDigits < mobileBand[0];
  const mobileTooLong = mobileDigits > mobileBand[1];
  const mobileInvalid = mobileTooShort || mobileTooLong;
  const passwordTooShort = form.password.length > 0 && form.password.length < 8;
  const confirmMismatch = form.confirm_password.length > 0 && form.confirm_password !== form.password;

  // Auto-set country_code when country changes.
  useEffect(() => {
    if (!form.country) { set('country_code', ''); return; }
    if (activeCountry) set('country_code', activeCountry.dial);
    // eslint-disable-next-line
  }, [form.country]);

  // Debounced early duplicate-email check. Runs as soon as a valid-looking
  // email has been entered so the returning-user panel appears before the
  // rest of the signup fields have been filled out.
  useEffect(() => {
    const email = form.email.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailCheckState('idle');
      return;
    }
    setEmailCheckState('checking');
    const timer = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/satisfinder/check-email?email=${encodeURIComponent(email)}`);
        const d = await r.json();
        if (d.exists) {
          setExistingAccountEmail(email);
          setExistingAccount(true);
          setEmailCheckState('exists');
        } else {
          setEmailCheckState('available');
        }
      } catch {
        setEmailCheckState('idle');
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [form.email]);

  // Poll country status when country changes.
  useEffect(() => {
    if (!form.country) { setCountryStatus(null); return; }
    (async () => {
      try {
        const r = await fetch(`${API}/satisfinder/country-status?country=${encodeURIComponent(form.country)}`);
        const d = await r.json();
        setCountryStatus(d.status || 'open');
      } catch { setCountryStatus('open'); }
    })();
  }, [form.country]);

  // Postal-code → city lookup. Debounced 400ms. Uses existing
  // /satisfinder/postal-area-suggest — returns rows keyed by postal_code.
  // We deduplicate on `city` so the user picks a locality, not an area.
  useEffect(() => {
    setPostalLookupState('idle');
    setCitySuggestions([]);
    if (!form.postal_code || !form.country) return;
    const timer = setTimeout(async () => {
      setPostalLookupState('loading');
      try {
        const q = new URLSearchParams({
          country: form.country,
          postal_code: form.postal_code,
        });
        const r = await fetch(`${API}/satisfinder/postal-area-suggest?${q.toString()}`);
        const d = await r.json();
        const rows = d.areas || [];
        // Distinct cities from the response.
        const seen = new Set();
        const distinct = [];
        for (const row of rows) {
          const c = (row.city || '').trim();
          if (!c || seen.has(c.toLowerCase())) continue;
          seen.add(c.toLowerCase());
          distinct.push(c);
        }
        setCitySuggestions(distinct);
        if (distinct.length === 1) {
          // Clear match → auto-populate.
          set('city', distinct[0]);
          setPostalLookupState('matched');
        } else if (distinct.length > 1) {
          setPostalLookupState('matched'); // multiple choices → show dropdown
          set('city', '');
        } else {
          // No match → allow manual entry. Clear any previously auto-populated
          // city so the satisFINDER doesn't submit stale data from a prior
          // postal-code guess.
          set('city', '');
          setPostalLookupState('no-match');
        }
      } catch {
        set('city', '');
        setPostalLookupState('no-match');
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [form.postal_code, form.country]);

  const countryBanner = useMemo(() => {
    if (!countryStatus || !form.country) return null;
    if (countryStatus === 'blocked') {
      return { tone: 'red',   text: 'satisFINDER® registration is not currently available in your country.' };
    }
    if (countryStatus === 'waitlist') {
      return { tone: 'amber', text: 'satisFIND® is not open for satisFINDER® registration in your country yet. Join the waitlist and we\u2019ll let you know when FINDs become available.' };
    }
    return { tone: 'green',  text: 'satisFINDER® registration is available in your country.' };
  }, [countryStatus, form.country]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim()) { toast.error('First name is required'); return; }
    if (!form.last_name.trim())  { toast.error('Last name is required'); return; }
    if (!form.country)           { toast.error('Please select your country'); return; }
    if (!form.postal_code.trim()) { toast.error('Postal code is required'); return; }
    if (!form.city.trim())       { toast.error('City is required'); return; }
    if (mobileInvalid) { toast.error('Please enter a valid mobile number for the selected country'); return; }
    if (form.password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (form.password !== form.confirm_password) { toast.error('Passwords do not match'); return; }
    if (!form.is_adult)     { toast.error('Please confirm you are 18 or older'); return; }
    if (!form.accept_legal) { toast.error('Please accept the legal agreements'); return; }
    setSubmitting(true);
    setExistingAccount(false);
    try {
      const res = await fetch(`${API}/satisfinder/signup`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          accept_honest_answers: acceptHonest,
          accept_code_of_conduct: acceptConduct,
          referral_code: referralCode || undefined,
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(d.detail || 'Sign-up failed'); return; }
      if (d.outcome === 'existing_account') {
        setExistingAccountEmail(form.email.trim().toLowerCase());
        setExistingAccount(true);
        return;
      }
      if (d.outcome === 'blocked')  { toast.error(d.message); return; }
      if (d.outcome === 'waitlist') { setWaitlistDone(true); toast.success('Added to waitlist'); return; }
      toast.success('Account created — check your email for a verification code.');
      onSignedUp(form.email);
    } catch (err) {
      toast.error('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (existingAccount) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16 text-center">
        <div className="bg-white rounded-2xl border border-[#E2E8F0] p-10 shadow-sm" data-testid="sf-existing-account">
          <div className="inline-flex items-center gap-2 text-[#B45309] bg-amber-50 border border-amber-200 rounded-full px-3 py-1 text-xs font-semibold">
            <AlertCircle className="w-3.5 h-3.5" /> Account already exists
          </div>
          <h2 className="font-['Montserrat'] text-2xl font-bold text-[#0F172A] mt-4">
            You already have a satisFINDER® account with this email.
          </h2>
          {existingAccountEmail && (
            <p className="text-sm text-[#64748B] mt-2 font-mono" data-testid="sf-existing-account-email">
              {existingAccountEmail}
            </p>
          )}
          <p className="text-[#475569] mt-3">Choose an option below to continue.</p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to={`/satisfinder/login${existingAccountEmail ? `?email=${encodeURIComponent(existingAccountEmail)}` : ''}`}
              className="w-full sm:w-auto bg-[#1A3E6F] hover:bg-[#12305A] text-white font-semibold py-3 px-6 rounded-lg text-sm"
              data-testid="sf-existing-account-login"
            >Log in</Link>
            <Link
              to={`/satisfinder/forgot-password${existingAccountEmail ? `?email=${encodeURIComponent(existingAccountEmail)}` : ''}`}
              className="w-full sm:w-auto bg-white border border-[#CBD5E1] hover:border-[#1A3E6F] text-[#0F172A] font-semibold py-3 px-6 rounded-lg text-sm"
              data-testid="sf-existing-account-reset"
            >Reset password</Link>
          </div>
          <button
            type="button"
            className="mt-6 text-xs text-[#64748B] hover:underline"
            onClick={() => {
              setExistingAccount(false);
              setExistingAccountEmail('');
              setEmailCheckState('idle');
              set('email', '');
            }}
            data-testid="sf-existing-account-different-email"
          >Use a different email</button>
        </div>
      </div>
    );
  }

  if (waitlistDone) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16 text-center">
        <div className="bg-white rounded-2xl border border-[#E2E8F0] p-10 shadow-sm">
          <h2 className="font-['Montserrat'] text-2xl font-bold text-[#0F172A]">You&apos;re on the waitlist.</h2>
          <p className="text-[#475569] mt-4">We&apos;ll email you when satisFIND® opens satisFINDER® registration in {form.country}.</p>
          <Link to="/satisfinder" className="inline-block mt-6 text-sm text-[#1A3E6F] hover:underline">Back to satisFINDER® home</Link>
        </div>
      </div>
    );
  }

  const bannerTone = countryBanner ? {
    green: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    amber: 'bg-amber-50 text-amber-800 border-amber-200',
    red:   'bg-rose-50 text-rose-800 border-rose-200',
  }[countryBanner.tone] : '';

  const disabled = submitting || countryStatus === 'blocked' || mobileInvalid || passwordTooShort || confirmMismatch;

  return (
    <div className="max-w-2xl mx-auto px-6 py-10">
      <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-sm p-8" data-testid="sf-signup-form">
        <h1 className="font-['Montserrat'] text-2xl lg:text-3xl font-bold">Become a satisFINDER®</h1>
        <p className="text-sm text-[#475569] mt-2">Create your account in under a minute.</p>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="First name" required>
              <input required value={form.first_name} onChange={(e) => set('first_name', e.target.value)} className={inputCls} data-testid="sf-input-first-name" autoComplete="given-name" />
            </Field>
            <Field label="Middle name">
              <input value={form.middle_name} onChange={(e) => set('middle_name', e.target.value)} className={inputCls} data-testid="sf-input-middle-name" autoComplete="additional-name" />
            </Field>
            <Field label="Last name" required>
              <input required value={form.last_name} onChange={(e) => set('last_name', e.target.value)} className={inputCls} data-testid="sf-input-last-name" autoComplete="family-name" />
            </Field>
          </div>

          <Field
            label="Email address"
            required
            helper={
              emailCheckState === 'checking' ? 'Checking…' :
              emailCheckState === 'available' ? 'Email available.' :
              undefined
            }
          >
            <input required type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className={inputCls} data-testid="sf-input-email" autoComplete="email" />
          </Field>

          <Field label="Country" required>
            <select value={form.country} onChange={(e) => set('country', e.target.value)} className={inputCls} data-testid="sf-input-country" autoComplete="country-name">
              <option value="">Select your country…</option>
              {COUNTRIES.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
            </select>
          </Field>

          {countryBanner && (
            <div className={`border rounded-lg px-4 py-3 text-sm ${bannerTone}`} data-testid="sf-country-banner">
              {countryBanner.text}
            </div>
          )}

          {countryStatus !== 'blocked' && (
            <>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Code" required>
                  <div className={`${inputCls} bg-[#F8FAFC] flex items-center gap-2 py-2`} data-testid="sf-input-country-code-display">
                    <span className="text-lg leading-none" aria-hidden="true" data-testid="sf-country-flag">{flag || '🌐'}</span>
                    <span className="font-mono text-sm text-[#0F172A]">{form.country_code || '+—'}</span>
                  </div>
                  {/* Hidden field preserves the country_code value in the DOM for form submissions/tests. */}
                  <input type="hidden" value={form.country_code} data-testid="sf-input-country-code" />
                </Field>
                <div className="col-span-2">
                  <Field
                    label="Mobile number"
                    required
                    helper={
                      mobileTooShort ? `Too short — expected ${mobileBand[0]}${mobileBand[0] !== mobileBand[1] ? `–${mobileBand[1]}` : ''} digits.` :
                      mobileTooLong  ? `Too long — expected ${mobileBand[0]}${mobileBand[0] !== mobileBand[1] ? `–${mobileBand[1]}` : ''} digits.` :
                      undefined
                    }
                  >
                    <input
                      required
                      inputMode="numeric"
                      value={form.mobile_number}
                      onChange={(e) => set('mobile_number', e.target.value.replace(/[^\d\s-]/g, ''))}
                      className={`${inputCls} ${mobileInvalid ? 'border-rose-400 focus:ring-rose-400 focus:border-rose-400' : ''}`}
                      data-testid="sf-input-mobile"
                      autoComplete="tel-national"
                    />
                  </Field>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Postal code" required helper={postalLookupState === 'loading' ? 'Looking up…' : postalLookupState === 'no-match' ? 'No match — please enter your city below.' : undefined}>
                  <input required value={form.postal_code} onChange={(e) => set('postal_code', e.target.value)} className={inputCls} data-testid="sf-input-postal" autoComplete="postal-code" />
                </Field>
                <Field label="City" required>
                  {citySuggestions.length > 1 ? (
                    <select required value={form.city} onChange={(e) => set('city', e.target.value)} className={inputCls} data-testid="sf-input-city-select">
                      <option value="">Select your city…</option>
                      {citySuggestions.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  ) : (
                    <input required value={form.city} onChange={(e) => set('city', e.target.value)} className={inputCls} data-testid="sf-input-city" autoComplete="address-level2" />
                  )}
                </Field>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field
                  label="Password"
                  required
                  helper={passwordTooShort ? 'Use at least 8 characters.' : 'Use at least 8 characters.'}
                >
                  <input
                    required
                    type="password"
                    minLength={8}
                    value={form.password}
                    onChange={(e) => set('password', e.target.value)}
                    className={`${inputCls} ${passwordTooShort ? 'border-rose-400 focus:ring-rose-400 focus:border-rose-400' : ''}`}
                    data-testid="sf-input-password"
                    autoComplete="new-password"
                  />
                </Field>
                <Field
                  label="Confirm password"
                  required
                  helper={confirmMismatch ? 'Passwords do not match.' : undefined}
                >
                  <input
                    required
                    type="password"
                    minLength={8}
                    value={form.confirm_password}
                    onChange={(e) => set('confirm_password', e.target.value)}
                    className={`${inputCls} ${confirmMismatch ? 'border-rose-400 focus:ring-rose-400 focus:border-rose-400' : ''}`}
                    data-testid="sf-input-confirm"
                    autoComplete="new-password"
                  />
                </Field>
              </div>

              <label className="flex items-start gap-2.5 pt-2">
                <input type="checkbox" checked={form.is_adult} onChange={(e) => set('is_adult', e.target.checked)} className="mt-1 accent-[#1A3E6F]" data-testid="sf-check-adult" />
                <span className="text-sm text-[#0F172A]">I confirm I am 18 years or older.</span>
              </label>
              <label className="flex items-start gap-2.5">
                <input type="checkbox" checked={form.accept_legal} onChange={(e) => set('accept_legal', e.target.checked)} className="mt-1 accent-[#1A3E6F]" data-testid="sf-check-legal" />
                <span className="text-sm text-[#0F172A]">
                  I agree to the{' '}
                  <Link to={legalUrl('terms_of_service', '/satisfinder/legal/terms-of-service')} target="_blank" className="text-[#1A3E6F] underline">Terms of Service</Link>{' '}and{' '}
                  <Link to={legalUrl('privacy_policy', '/satisfinder/legal/privacy-policy')} target="_blank" className="text-[#1A3E6F] underline">Privacy Policy</Link>.
                </span>
              </label>
            </>
          )}

          <button
            type="submit"
            disabled={disabled}
            className="w-full bg-[#1A3E6F] hover:bg-[#12305A] disabled:bg-[#94A3B8] disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg text-sm mt-2"
            data-testid="sf-submit-button"
          >
            {submitting ? 'Creating account…'
              : countryStatus === 'waitlist' ? 'Join the waitlist'
              : countryStatus === 'blocked'  ? 'Not available in your country'
              : 'Create my satisFINDER® account'}
          </button>
          <p className="text-center text-sm text-[#475569] pt-1">
            Already registered? <Link to="/satisfinder/login" className="text-[#1A3E6F] font-semibold hover:underline">Login</Link>
          </p>
        </form>
      </div>
    </div>
  );
}

const inputCls = 'w-full px-3 py-2.5 border border-[#CBD5E1] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1A3E6F] focus:border-[#1A3E6F] bg-white';

function Field({ label, required, helper, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-[#334155] mb-1">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      {children}
      {helper && <p className="text-[10.5px] text-[#64748B] mt-1 leading-snug">{helper}</p>}
    </div>
  );
}
